import React, { useState } from "react";
import { Building2, Users, FileText, ExternalLink, ChevronRight } from "lucide-react";
import { PageHero, PageBody } from "./_shared";
import { useFood } from "./FoodLayout";

const COLORS = [
  "from-[#1F6B3F] to-[#13532E]",
  "from-[#F4751F] to-[#C95507]",
  "from-[#13532E] to-[#0e3a22]",
  "from-[#C95507] to-[#A0440A]",
  "from-[#1F6B3F] to-[#0e3a22]",
  "from-[#E36213] to-[#A0440A]",
];

export default function Committees() {
  const data = useFood();
  const list = data.committees || [];
  const [activeId, setActiveId] = useState(list[0]?.id || null);

  const activeIdx = Math.max(0, list.findIndex((c) => c.id === activeId));
  const active = list[activeIdx] || null;
  const grad = COLORS[activeIdx % COLORS.length];

  return (
    <div data-testid="ft-committees">
      <PageHero eyebrow="Governance" title="Committees &amp; Cells" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white rounded-xl ring-1 ring-[#E6E2D6] p-12 text-[#4D5A52]">
            Committee details will appear here once published.
          </div>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-6">
            {/* Left: list */}
            <aside
              className="bg-white rounded-xl ring-1 ring-[#E6E2D6] md:sticky md:top-32 self-start overflow-hidden shadow-sm"
              data-testid="ft-committees-list"
            >
              <div className="px-4 py-3 border-b border-[#E6E2D6]">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#F4751F] font-semibold">Browse</div>
                <div className="font-serif text-lg text-[#0F1F17]">
                  All Committees ({list.length})
                </div>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto">
                {list.map((c, i) => {
                  const isActive = c.id === active?.id;
                  const itemGrad = COLORS[i % COLORS.length];
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(c.id)}
                        data-testid={`ft-committee-tab-${c.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive ? "bg-[#FFF1E5] border-[#F4751F]" : "border-transparent hover:bg-[#FCFAF4]"
                        }`}
                      >
                        <span className={`w-8 h-8 rounded-md bg-gradient-to-br ${itemGrad} text-white flex items-center justify-center shrink-0`}>
                          <Building2 size={14} strokeWidth={1.5} />
                        </span>
                        <span className={`flex-1 text-sm leading-snug ${isActive ? "text-[#0F1F17] font-semibold" : "text-[#4D5A52]"}`}>
                          {c.name}
                        </span>
                        <ChevronRight size={14} strokeWidth={1.5} className={isActive ? "text-[#F4751F]" : "text-[#E6E2D6]"} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: detail */}
            {active && (
              <article
                key={active.id}
                data-testid={`ft-committee-${active.id}`}
                className="bg-white rounded-xl overflow-hidden ring-1 ring-[#E6E2D6] shadow-sm"
              >
                <div className={`bg-gradient-to-r ${grad} p-5 text-white flex items-center gap-3`}>
                  <div className="w-10 h-10 rounded-md bg-white/20 backdrop-blur flex items-center justify-center">
                    <Building2 size={18} strokeWidth={1.5} />
                  </div>
                  <h2 className="font-serif text-xl leading-tight">{active.name}</h2>
                </div>
                <div className="p-6">
                  {active.objective_html && (
                    <div className="mb-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#F4751F] font-semibold mb-1.5">Objective</div>
                      <div
                        className="prose prose-sm prose-slate max-w-none text-[#4D5A52]"
                        dangerouslySetInnerHTML={{ __html: active.objective_html }}
                      />
                    </div>
                  )}
                  {active.has_functions && active.functions_html && (
                    <div className="mb-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#F4751F] font-semibold mb-1.5">Functions</div>
                      <div
                        className="prose prose-sm prose-slate max-w-none text-[#4D5A52]"
                        dangerouslySetInnerHTML={{ __html: active.functions_html }}
                      />
                    </div>
                  )}
                  {(active.members || []).length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#F4751F] font-semibold mb-2 inline-flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.5} /> Members ({active.members.length})
                      </div>
                      <ul className="divide-y divide-[#F4E9D6] border border-[#E6E2D6] rounded-md">
                        {active.members.map((m, idx) => (
                          <li
                            key={idx}
                            className="px-3 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 hover:bg-[#FCFAF4]"
                          >
                            <span className="font-semibold text-[#0F1F17]">{m.name}</span>
                            {m.department && (
                              <span className="text-[#4D5A52] text-xs">{m.department}</span>
                            )}
                            {m.designation && (
                              <span className="sm:ml-auto text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded bg-[#E6F3EB] text-[#13532E] font-bold">
                                {m.designation}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {active.has_pdf && active.pdf_url && (
                    <a
                      href={active.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm bg-[#FFF1E5] hover:bg-[#FFD9A8] text-[#C95507] px-3 py-2 rounded-md font-semibold"
                    >
                      <FileText size={14} strokeWidth={1.5} /> Open attached PDF
                      <ExternalLink size={11} strokeWidth={1.5} />
                    </a>
                  )}
                  {active.remarks && (
                    <div className="mt-5 text-xs text-[#4D5A52] italic">{active.remarks}</div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}
